import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaArrowRight } from 'react-icons/fa';
import Reveal from '../Reveal';
import ProjectMockup from './ProjectMockup';
import {
  PortfolioSection,
  Container,
  SectionHeader,
  SectionTag,
  SectionTitle,
  SectionSubtitle,
  CasesGrid,
  CaseStudy,
  CaseVisual,
  CaseBody,
  CaseEyebrow,
  CaseTitle,
  CaseDesc,
  CaseMetaGrid,
  CaseMetaCard,
  TagsRow,
  Tag,
  CtaRow,
  CtaButton,
} from './Portfolio.styles';

const caseStudies = [
  {
    key: 'p0',
    variant: 0,
    accent: '#d2a354',
    tags: [{ label: 'Next.js', color: '#f3f6f9' }, { label: 'TypeScript', color: '#7f93a8' }, { label: 'Checkout', color: '#c88b3a' }],
    eyebrow: {
      en: 'Commerce / launch surface',
      pl: 'Commerce / warstwa sprzedaży',
    },
    scope: {
      en: 'Storefront, cart and admin area',
      pl: 'Sklep, koszyk i panel administracyjny',
    },
    outcome: {
      en: 'Fast purchase path and scalable catalogue',
      pl: 'Szybka ścieżka zakupu i skalowalny katalog',
    },
    delivery: {
      en: 'Built for growth and content updates',
      pl: 'Gotowe pod rozwój i częste aktualizacje',
    },
  },
  {
    key: 'p1',
    variant: 1,
    accent: '#8fa1b5',
    tags: [{ label: 'React Native', color: '#a9bacb' }, { label: 'Expo', color: '#f3f6f9' }, { label: 'Push', color: '#c88b3a' }],
    eyebrow: {
      en: 'Mobile / one codebase',
      pl: 'Mobile / jeden codebase',
    },
    scope: {
      en: 'Cross-platform product for iOS and Android',
      pl: 'Cross-platformowy produkt na iOS i Androida',
    },
    outcome: {
      en: 'Clean workout flow with progress tracking',
      pl: 'Czytelny flow treningów i śledzenie postępów',
    },
    delivery: {
      en: 'Expo delivery with faster release rhythm',
      pl: 'Expo z szybszym rytmem publikacji wersji',
    },
  },
  {
    key: 'p4',
    variant: 4,
    accent: '#95a3b3',
    tags: [{ label: 'PWA', color: '#c88b3a' }, { label: 'TypeScript', color: '#7f93a8' }, { label: 'Offline', color: '#f3f6f9' }],
    eyebrow: {
      en: 'PWA / product behaviour',
      pl: 'PWA / zachowanie produktu',
    },
    scope: {
      en: 'Installable messaging experience in the browser',
      pl: 'Instalowalny komunikator działający w przeglądarce',
    },
    outcome: {
      en: 'Offline mode and notifications without app stores',
      pl: 'Offline i powiadomienia bez udziału app store',
    },
    delivery: {
      en: 'Web product that feels closer to native',
      pl: 'Produkt webowy, który zachowuje się bliżej natywnie',
    },
  },
];

const Portfolio: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith('pl') ? 'pl' : 'en';

  const labels = lang === 'pl'
    ? { scope: 'Zakres', outcome: 'Efekt', delivery: 'Realizacja' }
    : { scope: 'Scope', outcome: 'Outcome', delivery: 'Delivery' };

  return (
    <PortfolioSection>
      <Container>
        <Reveal>
          <SectionHeader>
            <div>
              <SectionTag>{t('portfolio.tag')}</SectionTag>
              <SectionTitle>
                {t('portfolio.title')} <span>{t('portfolio.titleGradient')}</span>
              </SectionTitle>
            </div>
            <SectionSubtitle>{t('portfolio.subtitle')}</SectionSubtitle>
          </SectionHeader>
        </Reveal>

        <CasesGrid>
          {caseStudies.map((p, index) => (
            <Reveal key={p.key} delay={index * 90}>
              <CaseStudy $reverse={index % 2 === 1}>
                <CaseVisual>
                  <ProjectMockup variant={p.variant} accent={p.accent} />
                </CaseVisual>

                <CaseBody>
                  <CaseEyebrow>{p.eyebrow[lang]}</CaseEyebrow>
                  <CaseTitle>{t(`portfolio.${p.key}.title`)}</CaseTitle>
                  <CaseDesc>{t(`portfolio.${p.key}.desc`)}</CaseDesc>

                  <CaseMetaGrid>
                    <CaseMetaCard>
                      <span>{labels.scope}</span>
                      <strong>{p.scope[lang]}</strong>
                    </CaseMetaCard>
                    <CaseMetaCard>
                      <span>{labels.outcome}</span>
                      <strong>{p.outcome[lang]}</strong>
                    </CaseMetaCard>
                    <CaseMetaCard>
                      <span>{labels.delivery}</span>
                      <strong>{p.delivery[lang]}</strong>
                    </CaseMetaCard>
                  </CaseMetaGrid>

                  <TagsRow>
                    {p.tags.map((tag) => (
                      <Tag key={tag.label} $color={tag.color}>
                        {tag.label}
                      </Tag>
                    ))}
                  </TagsRow>
                </CaseBody>
              </CaseStudy>
            </Reveal>
          ))}
        </CasesGrid>

        <Reveal delay={120}>
          <CtaRow>
            <CtaButton href="mailto:bart@ercomp.co.uk">
              {t('portfolio.cta')} <FaArrowRight size={13} />
            </CtaButton>
          </CtaRow>
        </Reveal>
      </Container>
    </PortfolioSection>
  );
};

export default Portfolio;
